import { navigateToAndResetPageAndSearch } from 'front-end/src/pages/analytics/helpers'
import React, { FC, useContext, useState } from 'react'
import { Box } from '@chakra-ui/react'
import { Column } from 'react-table'
import { Trans, t } from '@lingui/macro'
import { css } from '@emotion/react'

import {
  SqlOrderDirectionEnum,
  UsersActivityFilterEnum,
  UsersOverviewOrderColumn
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { formatDateAndTimeShort } from 'front-end/src/utils/dateHelpers'

import TableExtendedOld, {
  DEFAULT_TR_PROPS
} from '../../../components/common/table-extended-old/TableExtendedOld'
import UserAvatar from '../../../components/common/user/user-avatar/UserAvatar'
import { DownloadPeopleCSV } from './DownloadPeopleCSV'
import { IBaseOverviewFilter } from '../interfaces'
import { linkBuilder } from 'shared/linkBuilder'
import { Modal, ButtonLight, Tooltip } from 'front-end/src/components'
import { usePeopleOverviewTableQuery } from './People.codegen'
import { AnalyticsContext } from '../AnalyticsContext'
import { StatusArchivedGrey } from 'front-end/src/components/common/status/Status'
import { brandColors } from 'shared/basicPalette'
import IconInfo from 'front-end/src/components/common/icon/IconInfo'

export interface IOverviewFilter extends IBaseOverviewFilter {
  orderColumn: UsersOverviewOrderColumn
  userActivity?: UsersActivityFilterEnum
}

const PeopleOverviewTable: FC<{}> = () => {
  const { filterAsQueryVariables, filter, changeFilter } =
    useContext(AnalyticsContext)
  const [showExportCsvModal, setShowExportCsvModal] = useState<boolean>(false)
  const [peopleTableFilters, setPeopleTableFilters] = useState<IOverviewFilter>(
    {
      limit: 20,
      orderColumn: UsersOverviewOrderColumn.resourceViewsCount,
      orderDirection: SqlOrderDirectionEnum.DESC
    }
  )
  const peopleQueryFilters = {
    page: filter.page,
    search: filter.search,
    ...peopleTableFilters
  }
  const [isCsvFetching, setIsCsvFetching] = useState(false)

  const { data, loading } = usePeopleOverviewTableQuery({
    variables: {
      mainAnalyticsFilter: filterAsQueryVariables,
      peopleOverviewFilter: peopleQueryFilters,
      activePeopleOverviewFilter: {
        ...peopleQueryFilters,
        userActivity: UsersActivityFilterEnum.ACTIVE_ONLY
      },
      inactivePeopleOverviewFilter: {
        ...peopleQueryFilters,
        userActivity: UsersActivityFilterEnum.INACTIVE_ONLY
      }
    }
  })

  const tableColumsSourceWidth = 120
  const tableColumns: Array<Column<any>> = [
    {
      Header: '',
      accessor: 'user',
      sortable: false,
      maxWidth: 56,
      Cell: (row) => <UserAvatar user={row.value} />
    },
    {
      id: 'display_name',
      Header: t`Name`,
      accessor: 'user.display_name',
      maxWidth: 270,
      Cell: (row) => (
        <Box className="overflowEllipsis">
          <strong className="dark">{row.value}</strong>
          {!!row.original.user.deleted_at && (
            <>
              &nbsp;
              <StatusArchivedGrey />
            </>
          )}
        </Box>
      )
    },
    {
      Header: t`Views`,
      accessor: 'resourceViewsCount',
      maxWidth: 90,
      Cell: (row) => <span className="green">{row.value}</span>
    },
    {
      Header: (
        <>
          <Tooltip
            css={css`
              position: relative;
              top: 1px;
            `}
            text={t`This displays the completed pathways during the selected time
              period. For detailed information including incomplete and not
              started enrolments, please click on the individual user.`}
            color={brandColors.grey.mid}
          >
            <IconInfo />
          </Tooltip>{' '}
          <Trans>Pathways</Trans>
        </>
      ),
      maxWidth: 120,
      width: tableColumsSourceWidth,
      accessor: 'pathwayEnrolmentsCount'
    },
    {
      Header: (
        <>
          <Tooltip
            css={css`
              position: relative;
              top: 1px;
            `}
            text={t`This displays the completed resources during the selected time
              period. For detailed information including incomplete and not
              started enrolments, please click on the individual user.`}
            color={brandColors.grey.mid}
          >
            <IconInfo />
          </Tooltip>{' '}
          <Trans>Enrolments</Trans>
        </>
      ),
      maxWidth: 120,
      width: tableColumsSourceWidth,
      accessor: 'workbooksCount'
    },
    {
      Header: (
        <>
          <Tooltip
            css={css`
              position: relative;
              top: 1px;
            `}
            text={t`This displays the completed surveys during the selected time
              period. For detailed information including incomplete and not
              started surveys, please click on the individual user.`}
            color={brandColors.grey.mid}
          >
            <IconInfo />
          </Tooltip>{' '}
          <Trans>Surveys</Trans>
        </>
      ),
      maxWidth: 120,
      width: tableColumsSourceWidth,
      accessor: 'surveysCount'
    },
    {
      Header: (
        <>
          <Tooltip
            css={css`
              position: relative;
              top: 1px;
            `}
            text={t`This displays the completed certifications during the selected
              time period. For detailed information including incomplete and not
              started certifications, please click on the individual user.`}
            color={brandColors.grey.mid}
          >
            <IconInfo />
          </Tooltip>{' '}
          <Trans>Certifications</Trans>
        </>
      ),
      maxWidth: 120,
      width: tableColumsSourceWidth,
      accessor: 'certificationEnrolmentsCount'
    },
    {
      Header: (
        <>
          <Tooltip
            css={css`
              position: relative;
              top: 1px;
            `}
            text={t`This displays count of attended events during the selected time
              period. For detailed information about other event invitations,
              please click on the individual user.`}
            color={brandColors.grey.mid}
          >
            <IconInfo />
          </Tooltip>{' '}
          <Trans>Events</Trans>
        </>
      ),
      maxWidth: 120,
      width: tableColumsSourceWidth,
      accessor: 'attendedEventsCount'
    },
    {
      id: 'lastActivityAt',
      Header: t`Last active`,

      accessor: 'user.last_activity_at',
      Cell: (props) => (props.value ? formatDateAndTimeShort(props.value) : '')
    }
  ]

  let pagesCount
  if (data?.analytics.people.usersOverviewCount) {
    pagesCount = Math.ceil(
      data.analytics.people.usersOverviewCount / peopleQueryFilters.limit
    )
  } else {
    pagesCount = 1
  }

  return (
    <div>
      <TableExtendedOld
        loading={loading}
        manual
        data={data?.analytics.people.usersOverview}
        tableHeader={t`People`}
        columns={tableColumns}
        className="-highlight AnalyticsTable"
        defaultPageSize={peopleQueryFilters.limit}
        showPageSizeOptions={false}
        showSearchFilter
        resizable={false}
        minRows={5}
        pages={pagesCount}
        getTrProps={(_state, rowInfo) => ({
          onClick: () => {
            navigateToAndResetPageAndSearch(
              linkBuilder.analyticsPeopleDetail(rowInfo.row.user.id),
              changeFilter
            )
          },
          className: `clickable ${
            rowInfo && rowInfo.row.user.deleted_at ? 'archived' : ''
          }`,
          ...DEFAULT_TR_PROPS
        })}
        paginationContentAfter={
          typeof data?.analytics.people.usersOverviewCount === 'number' && (
            <ButtonLight
              onClick={() => {
                setShowExportCsvModal(true)
              }}
            >
              <Trans>Export data</Trans>
              {` `}
              (.csv)
            </ButtonLight>
          )
        }
        defaultSortDesc
        defaultSorted={[
          {
            id: peopleQueryFilters.orderColumn,
            desc:
              peopleQueryFilters.orderDirection === SqlOrderDirectionEnum.DESC
          }
        ]}
        onSortedChange={(newSorted) => {
          setPeopleTableFilters((prevState) => {
            const descendingOrder = newSorted[0] ? newSorted[0].desc : false

            return {
              ...prevState,
              orderColumn: newSorted[0]
                ? (newSorted[0].id as UsersOverviewOrderColumn)
                : UsersOverviewOrderColumn.display_name,
              orderDirection: descendingOrder
                ? SqlOrderDirectionEnum.DESC
                : SqlOrderDirectionEnum.ASC
            }
          })
        }}
        onPageChange={(newPage) => {
          changeFilter({ page: newPage + 1 })
        }}
        defaultSearch={peopleQueryFilters.search}
        page={peopleQueryFilters.page - 1}
        onSearchChange={(searchVal) => {
          changeFilter({ search: searchVal })
        }}
      />
      <Modal
        className={'ModalPeopleExportData twoColumnWide'}
        show={showExportCsvModal}
        onHide={() => setShowExportCsvModal(false)}
        controls={
          <>
            <ButtonLight onClick={() => setShowExportCsvModal(false)}>
              <Trans>Close</Trans>
            </ButtonLight>
          </>
        }
        header={t`Which data you want to export?`}
      >
        <Box>
          <DownloadPeopleCSV
            disabled={isCsvFetching}
            onFetchEnd={() => setIsCsvFetching(false)}
            onFetchStart={() => setIsCsvFetching(true)}
            className={'full-width'}
            peopleOverviewFilter={peopleQueryFilters}
            peopleCount={data?.analytics.people.usersOverviewCount || 0}
            title={`${t`Export all people`} (.csv)`}
          />
        </Box>
        <Box mt={8} mb={8}>
          <DownloadPeopleCSV
            disabled={isCsvFetching}
            onFetchEnd={() => setIsCsvFetching(false)}
            onFetchStart={() => setIsCsvFetching(true)}
            className={'full-width'}
            peopleOverviewFilter={{
              ...peopleQueryFilters,
              userActivity: UsersActivityFilterEnum.ACTIVE_ONLY
            }}
            peopleCount={data?.analytics.people.activeUsersOverviewCount || 0}
            title={`${t`Export only active people`} (.csv)`}
          />
        </Box>
        <Box>
          <DownloadPeopleCSV
            disabled={isCsvFetching}
            onFetchEnd={() => setIsCsvFetching(false)}
            onFetchStart={() => setIsCsvFetching(true)}
            className={'full-width'}
            peopleOverviewFilter={{
              ...peopleQueryFilters,
              userActivity: UsersActivityFilterEnum.INACTIVE_ONLY
            }}
            peopleCount={data?.analytics.people.inactiveUsersOverviewCount || 0}
            title={`${t`Export only inactive people`} (.csv)`}
          />
        </Box>
      </Modal>
    </div>
  )
}

export default PeopleOverviewTable
